import type { MarlinStoreDb } from "../infra/db";
import { cycleIndex, createGridUtils } from "../logic/layouts/grid-utils";
import { isVideoScreen } from "../logic/layouts/screenType";
import { screensInDirection } from "../logic/screenDirection";
import { SELECTED_CAMERA_KEY, withState, type SyncStore } from "../infra/state";
import type {
  GridId,
  ScreenArrangement,
  ScreenDirection,
  SelectionRecord,
} from "../types";

const KEYS = ["screenLayouts", "selected"] as const;

const navigator = (store: SyncStore) => {
  const { gridToUse } = createGridUtils();
  let focusScreenId: number | null = null;

  const getPositionColumn = (gridId: string): number => {
    const special: Record<string, number> = {
      leftHalf: 0,
      rightHalf: 1,
      fullScreen: 0,
    };
    if (special[gridId] !== undefined) return special[gridId];
    const parts = gridId.split("-");
    if (parts.length === 2) {
      const column = parseInt(parts[1], 10);
      return isNaN(column) ? 0 : column - 1;
    }
    return 0;
  };

  const layoutOf = (screenId: number) => {
    const layout = store.get("screenLayouts").find((l) => l.id === screenId);
    return layout && isVideoScreen(layout.screenType) ? layout : undefined;
  };

  const getVisiblePositions = (screenId: number): string[] => {
    const layout = layoutOf(screenId);
    if (!layout) return [];
    const { fullScreen, halfScreenLeft, halfScreenRight } = layout.screenData;
    const nonEmpty = Object.entries(gridToUse(layout))
      .filter(([, data]) => data !== null)
      .map(([gridId]) => gridId);
    if (fullScreen && nonEmpty.includes(fullScreen)) return [fullScreen];
    if (halfScreenLeft || halfScreenRight) {
      const focused = halfScreenLeft || (halfScreenRight as GridId);
      const focusedColumn = getPositionColumn(focused);
      return nonEmpty.filter(
        (gridId) =>
          gridId === focused || getPositionColumn(gridId) !== focusedColumn,
      );
    }
    return nonEmpty;
  };

  const getCamerasFromPosition = (
    screenId: number,
    gridId: string,
  ): number[] => {
    const layout = layoutOf(screenId);
    if (!layout) return [];
    const data = gridToUse(layout)[gridId as GridId];
    if (!data) return [];
    const { halfScreenLeft, halfScreenRight, nCams } = layout.screenData;
    if (gridId === halfScreenLeft || gridId === halfScreenRight)
      return data.cameras;
    return data.cameras.slice(0, nCams);
  };

  const findMachineIdByGridId = (
    screenId: number,
    gridId: string,
  ): number | null => {
    const layout = layoutOf(screenId);
    const data = layout ? gridToUse(layout)[gridId as GridId] : null;
    return data?.cageId && data?.machineId ? data.machineId : null;
  };

  const select = (selection: SelectionRecord): void => {
    store.set("selected", selection);
    focusScreenId = selection.screenId;
  };

  const selectFirstCameraOnScreen = (screenId: number): boolean => {
    const positions = getVisiblePositions(screenId);
    if (positions.length === 0) return false;
    const cameras = getCamerasFromPosition(screenId, positions[0]);
    if (cameras.length === 0) return false;
    const machineId = findMachineIdByGridId(screenId, positions[0]);
    if (machineId === null) return false;
    select({ screenId, cameraId: cameras[0], machineId });
    return true;
  };

  const navigateCamera = (
    sourceScreenId: number,
    direction: "next" | "previous",
  ): void => {
    const current = store.get("selected");
    if (!current || current.screenId !== sourceScreenId) {
      selectFirstCameraOnScreen(sourceScreenId);
      return;
    }
    const { screenId, cameraId } = current;
    const positions = getVisiblePositions(screenId);
    if (positions.length === 0) return;

    let positionIndex = -1;
    let cameraIndex = -1;
    for (let i = 0; i < positions.length && positionIndex === -1; i += 1) {
      const index = getCamerasFromPosition(screenId, positions[i]).indexOf(
        cameraId,
      );
      if (index !== -1) {
        positionIndex = i;
        cameraIndex = index;
      }
    }
    if (positionIndex === -1) {
      selectFirstCameraOnScreen(screenId);
      return;
    }

    const selectAt = (nextCameraId: number, index: number): boolean => {
      const machineId = findMachineIdByGridId(screenId, positions[index]);
      if (machineId === null) return false;
      select({ screenId, cameraId: nextCameraId, machineId });
      return true;
    };

    const cameras = getCamerasFromPosition(screenId, positions[positionIndex]);
    if (direction === "next") {
      if (
        cameraIndex < cameras.length - 1 &&
        selectAt(cameras[cameraIndex + 1], positionIndex)
      )
        return;
      const nextIndex = (positionIndex + 1) % positions.length;
      const nextCameras = getCamerasFromPosition(
        screenId,
        positions[nextIndex],
      );
      if (nextCameras.length > 0) selectAt(nextCameras[0], nextIndex);
    } else {
      if (cameraIndex > 0 && selectAt(cameras[cameraIndex - 1], positionIndex))
        return;
      const prevIndex =
        positionIndex === 0 ? positions.length - 1 : positionIndex - 1;
      const prevCameras = getCamerasFromPosition(
        screenId,
        positions[prevIndex],
      );
      if (prevCameras.length > 0)
        selectAt(prevCameras[prevCameras.length - 1], prevIndex);
    }
  };

  const visibleScreenIds = (): number[] =>
    store
      .get("screenLayouts")
      .filter((layout) => layout.isShowing)
      .map((layout) => layout.id);

  const navigateScreen = (
    direction: "next" | "previous",
    { order }: ScreenArrangement,
  ): void => {
    const current = store.get("selected");
    const showing = visibleScreenIds();
    const visible = [
      ...order.filter((id) => showing.includes(id)),
      ...showing.filter((id) => !order.includes(id)),
    ];
    let index = current ? visible.indexOf(current.screenId) : -1;
    for (let step = 0; step < visible.length; step += 1) {
      index = cycleIndex(index, visible.length, direction);
      if (selectFirstCameraOnScreen(visible[index])) return;
    }
  };

  const navigateScreenInDirection = (
    senderScreenId: number,
    direction: ScreenDirection,
    { positions }: ScreenArrangement,
  ): void => {
    const visible = Object.fromEntries(
      visibleScreenIds()
        .filter((id) => positions[id])
        .map((id) => [id, positions[id]]),
    );
    const selectedScreenId = store.get("selected")?.screenId;
    const fromScreenId =
      selectedScreenId !== undefined && visible[selectedScreenId]
        ? selectedScreenId
        : senderScreenId;
    screensInDirection(visible, fromScreenId, direction).some((id) =>
      selectFirstCameraOnScreen(id),
    );
  };

  return {
    select,
    navigateCamera,
    navigateScreen,
    navigateScreenInDirection,
    result: () => ({ focusScreenId }),
  };
};

export type SelectionResult = { focusScreenId: number | null };

export const getSelected = async (
  db: MarlinStoreDb,
): Promise<SelectionRecord | null> =>
  ((await db.settings.get(SELECTED_CAMERA_KEY))?.value as
    | SelectionRecord
    | undefined) ?? null;

export const getSelectedCamera = async (
  db: MarlinStoreDb,
  screenId: number,
) => {
  const selected = await getSelected(db);
  if (!selected || selected.screenId !== screenId) return null;
  return { cameraId: selected.cameraId, machineId: selected.machineId };
};

export const selectCamera = (
  db: MarlinStoreDb,
  screenId: number,
  cameraId: number,
  machineId: number,
): Promise<SelectionResult> =>
  withState(db, ["selected"], (store) => {
    store.set("selected", { screenId, cameraId, machineId });
    return { focusScreenId: screenId };
  });

export const selectNextCamera = (
  db: MarlinStoreDb,
  screenId: number,
): Promise<SelectionResult> =>
  withState(db, KEYS, (store) => {
    const nav = navigator(store);
    nav.navigateCamera(screenId, "next");
    return nav.result();
  });

export const selectPreviousCamera = (
  db: MarlinStoreDb,
  screenId: number,
): Promise<SelectionResult> =>
  withState(db, KEYS, (store) => {
    const nav = navigator(store);
    nav.navigateCamera(screenId, "previous");
    return nav.result();
  });

export const getVisibleScreenIds = async (
  db: MarlinStoreDb,
): Promise<number[]> =>
  (await db.screens.orderBy("id").toArray())
    .filter((layout) => layout.isShowing)
    .map((layout) => layout.id);

export const selectNextScreen = (
  db: MarlinStoreDb,
  arrangement: ScreenArrangement,
): Promise<SelectionResult> =>
  withState(db, KEYS, (store) => {
    const nav = navigator(store);
    nav.navigateScreen("next", arrangement);
    return nav.result();
  });

export const selectPreviousScreen = (
  db: MarlinStoreDb,
  arrangement: ScreenArrangement,
): Promise<SelectionResult> =>
  withState(db, KEYS, (store) => {
    const nav = navigator(store);
    nav.navigateScreen("previous", arrangement);
    return nav.result();
  });

export const selectScreenInDirection = (
  db: MarlinStoreDb,
  screenId: number,
  direction: ScreenDirection,
  arrangement: ScreenArrangement,
): Promise<SelectionResult> =>
  withState(db, KEYS, (store) => {
    const nav = navigator(store);
    nav.navigateScreenInDirection(screenId, direction, arrangement);
    return nav.result();
  });
